import { useCallback, useEffect, useRef } from 'preact/hooks';
import type { ModalUrl } from '../lib/modalUrl';

/** The container that mirrors each parameter, so two embeds on one page do not fight over it. */
const owners = new Map<string, string>();

/**
 * Mirror a detail modal in the URL (`?<param>=<id>`, see lib/modalUrl).
 *
 * The first mounted instance per parameter owns the URL: it opens the item a
 * pasted URL names, follows Back and Forward, and records opens and closes.
 * Other instances on the page still open their modal, without touching the URL.
 */
export function useModalUrl(
  link: ModalUrl,
  containerId: string,
  open: (id: string) => void,
  close: () => void,
) {
  const owns = useRef(false);
  const openRef = useRef(open);
  const closeRef = useRef(close);
  openRef.current = open;
  closeRef.current = close;

  useEffect(() => {
    if (owners.has(link.param)) return;
    owners.set(link.param, containerId);
    owns.current = true;
    const syncFromUrl = () => {
      const id = link.read(window.location.search);
      if (id) openRef.current(id);
      else closeRef.current();
    };
    const initial = link.read(window.location.search);
    if (initial) openRef.current(initial);
    window.addEventListener('popstate', syncFromUrl);
    return () => {
      window.removeEventListener('popstate', syncFromUrl);
      owners.delete(link.param);
      owns.current = false;
    };
  }, [link, containerId]);

  const push = useCallback((id: string) => { if (owns.current) link.push(id); }, [link]);
  const clear = useCallback(() => { if (owns.current) link.clear(); }, [link]);
  return { push, clear };
}
